import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { captureError, installErrorReporting } from "./lib/errorReporting/reporter";
import { sfxEngine } from "./lib/sfx/SfxEngine";
import { installUiSfx } from "./lib/sfx/uiFeedback";
import { installTooltips } from "./lib/tooltip";
import { startAssetPrecache } from "./lib/assetPrecache";
// The app's two display faces, self-hosted (see the file's own header for the why). Imported
// before `./index.css`, and loaded globally — not from a route component — so portaled/desktop
// chrome resolves the faces too.
import "./fonts.css";
import "./index.css";
// The GMS//OS brutalist palette + textures as inert `:root` custom properties — one source of
// truth for every brutalist surface. Loaded globally (not via a route component) so portaled
// brutalist chrome resolves it too.
import "./routes/home/brutalist-tokens.css";

// FIRST, before anything else can throw: install the uncaught-error handlers and drain
// whatever the previous page load left queued. Everything below this line is observable;
// everything above it is not, which is why this sits at the top of the app's real work.
installErrorReporting();

const queryClient = new QueryClient();

// Arm the audio unlock at the earliest legal moment. This installs a one-shot first-gesture
// listener; it does NOT create an AudioContext, which autoplay policy forbids outside a gesture.
// Must run BEFORE installUiSfx() so the unlock listener is in place ahead of the first click —
// arming it from the first sound instead is measurably too late and makes that first click
// silent. See SfxEngine.prime()'s doc for the measurement.
sfxEngine.prime();

// App-wide UI click SFX — one delegated document listener, mounted once for the app's lifetime.
// See lib/sfx/uiFeedback.ts.
installUiSfx();

// App-wide tooltips for icon-only controls — one delegated layer reading `data-tip` attributes.
// See lib/tooltip.ts.
installTooltips();

// Warm the vendored chassis art in the background so a portrait never pops in late mid-encounter.
// Deliberately called here rather than from a component mount effect: this arms the sweep, it does
// not run it — the fetching is deferred to an idle callback inside, and declines entirely on a
// metered or slow connection. See the module header for why it is `new Image()` and why
// `public/tokens/` is warmed per roster rather than at boot.
startAssetPrecache();

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("missing #root");

createRoot(rootEl, {
  // React render errors never reach `window.onerror`, so the global handlers installed above
  // cannot see them — these two root options are the only way to observe a render crash, and
  // the only source of a `componentStack` (which names the component, where a minified stack
  // just names the bundle).
  //
  // Chosen over a root `<ErrorBoundary>` deliberately: a boundary has to *render* something, so
  // adding one would change what the user sees on a crash. These hooks observe without altering
  // behavior at all.
  onUncaughtError: (error, info) => captureError({ source: "react", thrown: error, componentStack: info.componentStack }),
  onCaughtError: (error, info) => captureError({ source: "react", thrown: error, componentStack: info.componentStack }),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
